import { useState } from "react";

import { Element, useEditor, useNode } from "@craftjs/core";
import { MoveHorizontal, MoveVertical, PlusIcon, Rows2 } from "lucide-react";
import { cn } from "twyne-new.ui";

import { define, prop, variant } from "@/components/builder/utils";

import BasePreview, { Action } from "./base";

import { PlaceholderBlock } from ".";

function FlexPreview({ children, properties, properties: { flexRows }, ...props }) {
  const { id } = useNode()
  const { actions, query: { createNode } } = useEditor();

  const handleAddItem = () => {
    if (!children) return
    actions.add(createNode(<Element is={PlaceholderBlock} canvas />), id)
  }

  return <BasePreview properties={properties} {...props} customActions={<>
    <Action onClick={handleAddItem} className='rounded-full! p-1!'>
      <PlusIcon size={12} />
    </Action>
  </>}>
    <div>
      {children}
      {Array.from({ length: flexRows }).map((_, idx) => <Element id={`placeholder-${idx}`} is={PlaceholderBlock} key={idx} canvas />)}
    </div>
  </BasePreview>
}

const MAX_ROWS = 6

function VariantPreview({ onChangeProps, label, flex: { direction } }) {
  const [selected, setSelected] = useState(-1)
  const [hovered, setHovered] = useState(false)

  const isColumns = direction === 'flex-row'
  const Icon = isColumns ? MoveHorizontal : MoveVertical
  const count = selected + 1
  const caption = hovered && count ? `${count} ${isColumns ? 'column' : 'row'}${count > 1 ? 's' : ''}` : 'pick a count'

  const handleChange = ridx => () => {
    setSelected(ridx)
    onChangeProps({
      flexRows: ridx + 1,
    })
  }

  return <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/60'>
    <div className='mb-3 flex items-center justify-between gap-2'>
      <span className='flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100'>
        <Icon size={16} className='text-gray-500 dark:text-gray-400' />
        {label}
      </span>
      <span className={cn('font-mono text-sm transition-colors', hovered ? 'text-blue-500 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400')}>
        {caption}
      </span>
    </div>
    <div
      className={cn('flex gap-2', direction)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {Array
        .from({ length: MAX_ROWS })
        .map((_, ridx) => {
          const active = hovered && ridx <= selected

          return <div
            key={`${ridx}`}
            onMouseEnter={handleChange(ridx)}
            className={cn('rounded-md border transition-colors duration-200', isColumns ? 'h-12 flex-1' : 'h-6 w-full', {
              'border-blue-500 bg-blue-500': active,
              'border-gray-300 bg-gray-100 dark:border-gray-700 dark:bg-gray-800': !active,
            })}
          />
        })}
    </div>
  </div>
}

export default define('flex', FlexPreview)
  .canvas()
  .palete(<Rows2 size={16} />, 'Flex', 'Structures')
  .property(prop.stylePattern())
  .property(prop.new('Variant', 'variant').hide().own())
  .property(prop.predefined.customClass())
  .property(...prop.predefined.layout())
  .property(...prop.predefined.appearance())
  .property(...prop.predefined.container())
  .property(prop.predefined.attributes())
  .variant(variant(VariantPreview, null)
    .add('col', 'Col', {
      layoutType: 'flex',
      flex: { direction: 'flex-row', align: 'items-center', distribute: 'justify-center' },
    }, { card: true })
    .add('row', 'Row', { flex: { direction: 'flex-col' } }, { card: true }))
  .defaultProps({
    layoutType: 'flex',
    flex: { gap: '2' },
  })
  .ignoreResetProps(['variant', 'flexRows', 'layoutType', 'flex.direction', 'flex.align', 'flex.distribute'])
  .build()
